import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Video, ChevronDown, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useCreatePost } from '@/features/social/hooks/useCreatePost';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { ImageUpload, ImageUploadResult } from '@/components/media/ImageUpload';
import { VideoUpload, VideoUploadResult } from '@/components/media/VideoUpload';
import { MediaPreview, MediaType } from '@/components/media/MediaPreview';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { getInitials } from '@/utils/avatarUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const MAX_CHARS = 2000;

interface ProfileSnippet {
  full_name: string | null;
  belt_rank: string | null;
  profile_picture_url: string | null;
  username: string | null;
  academy_name: string | null;
}

const TOOLBAR_HEIGHT_PX = 56;

const CreatePost = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const createPost = useCreatePost();

  const audienceOptions = [
    { value: 'public', label: t('sossial.audience_whole_community') },
    { value: 'connections', label: t('sossial.audience_only_connections') },
  ];

  const [content, setContent] = useState('');
  const [toolbarBottom, setToolbarBottom] = useState(0);
  const [visibility, setVisibility] = useState('public');
  const [media, setMedia] = useState<{ url: string; type: MediaType } | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileSnippet | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Fetch user's own profile for the header row
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('full_name, belt_rank, profile_picture_url, username, academies!main_academy_id(name)')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          const academyName = Array.isArray(data.academies)
            ? (data.academies[0] as any)?.name ?? null
            : (data.academies as any)?.name ?? null;
          setProfile({ ...data, academy_name: academyName });
        }
      });
  }, [user?.id]);

  // Keep media toolbar above the on-screen keyboard (visual viewport)
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const syncToolbarPosition = () => {
      const keyboardOffset = window.innerHeight - viewport.height - viewport.offsetTop;
      setToolbarBottom(Math.max(0, keyboardOffset));
    };

    viewport.addEventListener('resize', syncToolbarPosition);
    viewport.addEventListener('scroll', syncToolbarPosition);
    syncToolbarPosition();

    return () => {
      viewport.removeEventListener('resize', syncToolbarPosition);
      viewport.removeEventListener('scroll', syncToolbarPosition);
    };
  }, []);

  // Auto-expand textarea
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length > MAX_CHARS) return;
    setContent(value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const isDirty = content.trim().length > 0 || !!media;
  const canPost = isDirty && !createPost.isPending && !mediaLoading;

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleDiscard = () => {
    setShowDiscardDialog(false);
    navigate(-1);
  };

  const handleMediaError = (message: string) => {
    toast({ title: message, variant: 'destructive' });
  };

  const handleImageUpload = (result: ImageUploadResult) => {
    setMedia({ url: result.url, type: result.type });
  };

  const handleVideoUpload = (result: VideoUploadResult) => {
    setMedia({ url: result.url, type: 'video' });
  };

  const handleSubmit = useCallback(async () => {
    if (!canPost) return;

    const isOffline = !navigator.onLine;

    try {
      await createPost.mutateAsync({
        content: content.trim(),
        visibility,
        ...(media ? { media_url: media.url, media_type: media.type } : {}),
      });
      navigate('/sossial', { replace: true });
    } catch (err) {
      const message = isOffline
        ? 'Sua conexão tá travando. Seu post está salvo aqui — quando voltar pra área coberta, é só tentar de novo.'
        : 'Não conseguimos publicar seu post. Guarda o texto e tenta de novo em um momento.';
      toast({ title: message, variant: 'destructive', duration: 6000 });
    }
  }, [canPost, content, visibility, media, createPost, navigate, toast]);

  const audienceLabel =
    audienceOptions.find((o) => o.value === visibility)?.label ?? t('sossial.audience_whole_community');
  const belt = profile?.belt_rank ?? 'white';
  const beltDisplay = belt.charAt(0).toUpperCase() + belt.slice(1) + ' Belt';
  const academyInfo = [beltDisplay, profile?.academy_name].filter(Boolean).join(' · ');
  const initials = getInitials(profile?.full_name ?? '');

  return (
    <div className="min-h-screen flex flex-col bg-[#160C0C]">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 pb-px border-b border-white/[0.06] h-14 bg-[rgba(22,12,12,0.92)] sticky top-0 z-10">
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        <span className="flex-1 text-center font-bold text-base text-white">{t('sossial.new_post')}</span>
        <button
          onClick={handleSubmit}
          disabled={!canPost}
          className={`h-8 px-4 rounded-full text-[13px] font-semibold transition-all ${
            canPost
              ? 'bg-sossiety-red text-white hover:bg-sossiety-red-hover active:scale-95'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
        >
          {createPost.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
        </button>
      </div>

      {/* Author row */}
      <div className="flex items-center gap-3 px-5 pt-4 pb-2">
        <FramedAvatar
          src={profile?.profile_picture_url ?? undefined}
          initials={initials}
          beltRank={belt}
          size={34}
          frameSize={{ w: 48, h: 52 }}
          username={profile?.username ?? undefined}
          isManual
        />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-white leading-tight truncate">
            {profile?.full_name ?? ''}
          </p>
          {academyInfo && (
            <p className="text-[11px] text-[#8d8d8d] leading-tight truncate">{academyInfo}</p>
          )}
        </div>
        {/* Audience selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1 h-6 px-3 rounded-full bg-[#231a1a] border border-white/[0.06] text-[11px] text-white/70 whitespace-nowrap hover:bg-white/5 transition-colors">
              {audienceLabel}
              <ChevronDown className="w-3 h-3 opacity-60 ml-0.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#231a1a] border-white/10">
            {audienceOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => setVisibility(opt.value)}
                className={`text-[13px] cursor-pointer ${
                  visibility === opt.value ? 'text-sossiety-red' : 'text-white/80'
                }`}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Media preview */}
      {media && (
        <div className="px-5 pb-3">
          <MediaPreview
            url={media.url}
            type={media.type}
            onRemove={() => setMedia(null)}
          />
        </div>
      )}

      {/* Textarea */}
      <div
        className="flex-1 px-5"
        style={{ paddingBottom: TOOLBAR_HEIGHT_PX + toolbarBottom }}
      >
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          placeholder={t('sossial.content_placeholder')}
          className="w-full bg-transparent text-[15px] text-white placeholder:text-[#8d8d8d] resize-none outline-none leading-[1.5] min-h-[120px]"
          autoFocus
        />
      </div>

      {/* Bottom toolbar — fixed to visible viewport bottom (above keyboard) */}
      <div
        className="fixed left-0 right-0 z-20 border-t border-white/[0.06] bg-[#160C0C] px-4 h-14 flex items-center gap-1"
        style={{ bottom: toolbarBottom }}
      >
        <ImageUpload
          onUpload={handleImageUpload}
          onError={handleMediaError}
          onLoadingChange={setMediaLoading}
        >
          {(trigger) => (
            <button
              onClick={trigger}
              disabled={!!media || mediaLoading}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors disabled:opacity-40"
              aria-label="Add image or GIF"
            >
              {mediaLoading ? (
                <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
              ) : (
                <Image className="w-5 h-5 text-white/60" />
              )}
            </button>
          )}
        </ImageUpload>

        <VideoUpload
          onUpload={handleVideoUpload}
          onError={handleMediaError}
          onLoadingChange={setMediaLoading}
        >
          {(trigger) => (
            <button
              onClick={trigger}
              disabled={!!media || mediaLoading}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors disabled:opacity-40"
              aria-label="Add video"
            >
              <Video className="w-5 h-5 text-white/60" />
            </button>
          )}
        </VideoUpload>

        <span className="ml-auto text-[11px] text-[#8d8d8d]">
          {content.length} / {MAX_CHARS}
        </span>
      </div>

      {/* Discard draft dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent className="bg-[#231a1a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Descartar rascunho?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Seu texto e mídia serão perdidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 border-0 text-white hover:bg-white/20">
              Continuar editando
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              className="bg-sossiety-red text-white hover:bg-sossiety-red-hover"
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreatePost;
